<template>
    <div class="flex px-[2px] flex-col w-full">
        <InputLabel v-if="label" :id="id" :value="label" :description="description" :required="required"
            :disabled="disabled" class="mb-2" />
        <Listbox v-slot="{ open }" :modelValue="modelValue"
            @update:modelValue="value => $emit('update:modelValue', value)" :multiple="multiple">
            <div class="relative w-full" ref="reference">
                <ListboxButton ref="selectPickerButton" :disabled="disabled" :class="[
                    {
                        'ring-input-focus-border ring-1 border-input-focus-border':
                            open,
                    },
                    { 'ring-transparent': !open && !!error },
                    { 'ring-red-500 border-danger': !!error },
                    { 'border-input-border': !error },
                    {
                        'hover:bg-input-hover bg-input-background':
                            !disabled,
                    },
                    {
                        'opacity-50 cursor-not-allowed bg-gray-50':
                            disabled,
                    },
                    'focus:ring-input-focus-border focus:ring-1 focus:outline-none focus:border-input-focus-border',
                    'w-full flex items-center justify-between text-base rounded-input border p-input transition duration-button-hover',
                ]">
                    <p :class="[
                        { 'font-semibold': hasValue },
                        { 'text-input-muted': !hasValue },
                        'truncate flex items-center gap-x-2',
                    ]">
                        <span v-text="title" />

                        <TextTooltip v-if="currentDescription" :text="currentDescription"
                            :maxWidth="wideTooltip ? 'max-w-96' : 'max-w-64'">
                            <div
                                class="p-1 bg-primary text-primary-text hover:bg-primary-hover transition-colors duration-button-hover cursor-help rounded-full">
                                <PhQuestionMark class="size-3 -m-px" weight="bold" />
                            </div>
                        </TextTooltip>
                    </p>
                    <p class="flex items-center">
                        <PhCaretDown class="h-6 w-6" weight="bold" />
                    </p>
                </ListboxButton>

                <Teleport :to="teleportTarget || 'body'">
                    <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100"
                        leave-to-class="opacity-0">
                        <ListboxOptions ref="floating" :style="[floatingStyles, dropDownWidth]"
                            class="fixed shadow-popover overflow-hidden border scrollbar-gray rounded-popover bg-input-background z-popover divide-y focus:ring-input-focus-border focus:ring-1 focus:outline-none focus:border-input-focus-border'">
                            <div v-if="multiple && useSelectButtons"
                                class="flex justify-end items-center gap-x-4 w-full px-2 py-1.5 text-sm">
                                <button @click="selectAll" v-text="$lang.get('Select all')" class="underline-hover" />
                                <button @click="deSelectAll" v-text="$lang.get('Deselect all')"
                                    class="underline-hover" />
                            </div>
                            <div class="max-h-60 overflow-y-auto">
                                <ListboxOption v-slot="{ active, selected }" v-for="option in allOptions"
                                    :key="option.value" :value="option.value" class="cursor-pointer">
                                    <div :class="[
                                        {
                                            'bg-input-active-option text-input-active-option-text':
                                                selected,
                                        },
                                        {
                                            'bg-input-hover ':
                                                !selected && active,
                                        },
                                        'transition-colors duration-200 p-4 border-input-border',
                                    ]">
                                        <div class="flex justify-between items-center gap-x-3">
                                            <div class="flex items-center gap-x-2">
                                                <p :class="{
                                                    italic:
                                                        option.value ==
                                                        null &&
                                                        !nullValueOptionName,
                                                }" v-text="option.name" />

                                                <TextTooltip v-if="option.description" :text="option.description"
                                                    :contrastBorder="selected" :maxWidth="wideTooltip
                                                        ? 'max-w-[28rem]'
                                                        : 'max-w-64'
                                                        ">
                                                    <div :class="[
                                                        {
                                                            'bg-primary-text text-primary':
                                                                selected,
                                                        },
                                                        {
                                                            'bg-primary text-primary-text hover:bg-primary-hover':
                                                                !selected,
                                                        },
                                                        'p-1 transition-colors duration-button-hover cursor-help rounded-full',
                                                    ]">
                                                        <PhQuestionMark class="size-3 -m-px" weight="bold" />
                                                    </div>
                                                </TextTooltip>
                                            </div>

                                            <div v-if="multiple"
                                                class="flex-shrink-0 border size-5 rounded bg-input-background flex items-center justify-center">
                                                <PhCheck v-if="selected" class="size-6 text-primary" weight="bold" />
                                            </div>
                                        </div>
                                    </div>
                                </ListboxOption>
                            </div>
                        </ListboxOptions>
                    </transition>
                </Teleport>
            </div>
        </Listbox>

        <VerticalSlideTransition :show="disabled && !!disabledText">
            <p class="text-input-muted text-sm mt-2" v-text="disabledText" />
        </VerticalSlideTransition>
        <InputError :message="error" class="mt-2" />
    </div>
</template>

<script>
import {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
} from '@headlessui/vue'
import { ref } from 'vue'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    autoPlacement,
} from '@floating-ui/vue'
import {
    PhCaretDown,
    PhCheck,
    PhQuestionMark,
    PhQuestion,
} from '@phosphor-icons/vue'
import InputLabel from '@/Components/FormElements/InputLabel.vue'
import InputError from '@/Components/FormElements/InputError.vue'
import TextTooltip from '@/Components/Elements/TextTooltip.vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
export default {
    name: 'SelectPicker',
    emits: ['update:modelValue'],
    expose: ['focusMethod'],

    components: {
        Listbox,
        ListboxButton,
        ListboxOptions,
        ListboxOption,
        PhCaretDown,
        PhCheck,
        PhQuestionMark,
        PhQuestion,
        InputLabel,
        InputError,
        TextTooltip,
        VerticalSlideTransition,
    },

    props: {
        id: {
            type: [String, Number],
            required: true,
        },
        modelValue: {
            type: [Number, String, Array, null],
            required: true,
            validator: (value, props) => {
                if (props.multiple && !Array.isArray(value)) {
                    console.error(
                        'If the select is multiple, the modelValue must be an array'
                    )
                    return false
                }
                return true
            },
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        description: {
            type: String,
        },
        placeholder: {
            type: String,
        },
        error: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledText: {
            type: String,
            default: null,
        },
        useSelectButtons: {
            type: Boolean,
            default: true,
        },
        width: {
            type: String,
            default: null,
        },
        alignment: {
            type: String,
            default: 'center',
            validator: (value) => {
                return ['left', 'center', 'right'].includes(value)
            },
        },
        wideTooltip: {
            type: Boolean,
            default: false,
        },
        setDefaultValue: {
            type: Boolean,
            default: false,
        },
        showNullValue: {
            type: Boolean,
            default: false,
        },
        nullValueOptionName: {
            type: String,
            required: false,
        },
        nullValueOptionDescription: {
            type: String,
            required: false,
        },
        teleportTarget: {
            type: String,
            required: false,
        },
    },

    data() {
        return {
            dropDownWidth: 'width: 100%',
        }
    },

    mounted() {
        this.resizeObserver = new ResizeObserver(this.updateDropdownWidth)
        this.resizeObserver.observe(this.$refs.reference)

        if (this.setDefaultValue && this.modelValue === null) {
            let foundDefault = this.allOptions.find((opt) => opt.default)
            if (foundDefault) {
                this.$emit('update:modelValue', foundDefault.value)
            }
        }
    },

    beforeUnmount() {
        if (this.resizeObserver && this.$refs.reference) {
            this.resizeObserver.disconnect()
        }
    },

    computed: {
        /**
         * Returns all options, including the null option if showNullValue is true
         */
        allOptions() {
            let options = [...this.options]

            if (this.showNullValue) {
                options = [
                    {
                        value: null,
                        name:
                            this.nullValueOptionName ||
                            this.$lang.get('Nothing selected'),
                        description: this.nullValueOptionDescription || null,
                    },
                    ...options,
                ]
            }

            return options
        },

        placeholderText() {
            return this.placeholder
                ? this.placeholder
                : this.multiple
                    ? this.$lang.get('Select option(s)')
                    : this.$lang.get('Select an option')
        },

        /**
         * Returns true if the modelValue is not null or an empty array
         */
        hasValue() {
            if (this.multiple) {
                return this.modelValue && this.modelValue.length > 0
            }
            if (this.showNullAsSelectedIfSelected) {
                return true
            }
            return !!this.modelValue
        },

        /**
         * This is to decide if we want to show the null option as selected if the modelValue is null
         * We only do this if the null option has a name or description
         */
        showNullAsSelectedIfSelected() {
            return (
                this.modelValue === null &&
                this.showNullValue &&
                (this.nullValueOptionName || this.nullValueOptionDescription)
            )
        },

        /**
         * Returns the description of the currently selected option
         */
        currentDescription() {
            if (!this.multiple) {
                const found = this.allOptions.find(
                    (option) => option.value == this.modelValue
                )
                return found ? found.description : null
            }
        },

        /**
         * Returns the title of the currently selected option
         */
        title() {
            if (!this.hasValue || !this.allOptions) {
                return this.placeholderText
            }
            if (this.multiple) {
                const found = this.allOptions.filter((option) =>
                    this.modelValue.includes(option.value)
                )
                if (found.length > 0) {
                    if (found.length === 1) {
                        return found[0].name
                    } else if (found.length >= 3) {
                        return `${found.length} valda`
                    } else {
                        return found.map((option) => option.name).join(', ')
                    }
                } else {
                    return this.placeholderText
                }
            }

            const found = this.allOptions.find(
                (option) => option.value == this.modelValue
            )

            return found ? found.name : this.placeholderText
        },
    },

    setup(props) {
        const reference = ref(null)
        const floating = ref(null)
        let allowedPlacements = []

        if (props.alignment == 'left') {
            allowedPlacements = ['bottom-end', 'top-end']
        } else if (props.alignment == 'center') {
            allowedPlacements = ['bottom', 'top']
        } else if (props.alignment == 'right') {
            allowedPlacements = ['bottom-start', 'top-start']
        }

        const { floatingStyles, placement, middlewareData } = useFloating(
            reference,
            floating,
            {
                middleware: [
                    flip({
                        fallbackPlacements: ['bottom', 'top'],
                        fallbackStrategy: 'initialPlacement',
                        crossAxis: false, // Prevent cross-axis flipping
                        padding: 80,
                    }),
                    offset({
                        mainAxis: 5,
                    }),
                ],
                strategy: 'fixed',
                whileElementsMounted: autoUpdate,
            }
        )

        return {
            placement,
            reference,
            floating,
            floatingStyles,
            middlewareData,
        }
    },

    methods: {
        updateDropdownWidth() {
            if (this.width) {
                this.dropDownWidth =
                    'width: ' +
                    this.width +
                    '; min-width: ' +
                    this.$refs.reference.offsetWidth +
                    'px'
            } else {
                this.dropDownWidth =
                    'width: ' + this.$refs.reference.offsetWidth + 'px'
            }
        },

        selectAll() {
            this.$emit(
                'update:modelValue',
                this.allOptions.map((option) => option.value)
            )
        },

        deSelectAll() {
            this.$emit('update:modelValue', [])
        },

        focusMethod() {
            this.$refs.selectPickerButton.el.focus()
        },

        isSelected(value) {
            if (this.multiple) {
                return this.modelValue.some((v) => v == value)
            }
            return this.modelValue == value
        },
    },
}
</script>
